import type { PersistedTab } from 'apiConnector/tabs/tabs.model';
import { IdType } from 'epic/common-form/ReloadFormRequest';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { ProcessingChain } from 'models/ProcessingChain';
import type { ProductVariation, SelectedProductVariation } from 'models/productsReferential';
import type { IntlShape } from 'react-intl';
import { addonQuoteRemoved } from 'state/addonForm/addonForm.action';
import { batchFormQuoteReloadRequested } from 'state/batchForm/batchForm.action';
import { batchFormsAllDeletedThunk } from 'state/batchForm/batchForm.thunks';
import { getBlotterPanelItem } from 'state/blotters/blotterPanel/blotterPanel.selector';
import { dynamicQuoteRemovedThunk } from 'state/dynamic/dynamicForm.thunks';
import { notUsedQuoteCleanedThunk } from 'state/quotes/quotes.thunks';
import {
  addonSelector,
  getAvailablePayoffTypes,
  getBatchTabIdToReloadForm,
  getDynamicFormInfos,
  getFormEnrichedData,
  getStaticFormInfos,
  getUserPreferencesSelectedOnBehalfClient,
} from 'state/selectors';
import type { Thunk } from 'state/state.common';
import { staticQuoteRemovedThunk } from 'state/static/staticForm.thunks';
import { secondaryMarketPostEventWorkspaceRemoveTab } from 'state/workspaces/secondary-market/post-event/secondaryMarketPostEvent.actions';
import { secondaryMarketQuoteWorkspaceRemoveTab } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.actions';
import { getProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.selector';
import { getTabType } from 'state/workspaces/tabs/tabs.selector';
import type { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';
import {
  getSelectedProductVariation,
  getWorkspaceActiveTabId,
  getWorkspaceTabIds,
} from 'state/workspaces/workspaces/workspaces.selector';

import {
  batchWorkspaceProductSelectedThunk,
  batchWorkspaceTabProductSelectionSubmittedThunk,
} from './batch/tabContent/tabContent.thunks';
import type {
  SecondaryMarketPostEventTabType,
  SecondaryMarketQuotesTabType,
  TabsForWorkspace,
  TabType,
  WorkspaceType,
} from './model';
import { ordersBasketTabContentFetchRequested } from './ordersBasket/tabContent/tabContent.actions';
import {
  workspacePersistedTabsApplied,
  workspaceResetTabRequested,
  workspaceRfqLoadRequested,
  workspaceTabActivated,
  workspaceTabCreated,
  workspaceTabDeleteSessionRequested,
  workspaceTabPersistRequested,
  workspaceTabProductSelectionSubmitted,
  workspaceTabProductVariationSelected,
  workspaceTabQuoteRepriced,
  workspaceTabRemoved,
  workspaceTabsAllRemoved,
  workspaceTabsFetched,
} from './workspace.actions';
import { isAutoCreateTabWorkspace } from './workspaces.utils';
import { workspaceProductVariationSelected } from './workspaces/workspace.actions';

export function canWorkspaceBeEmpty(type: WorkspaceType): boolean {
  switch (type) {
    case 'secondaryMarketPostEvents':
    case 'secondaryMarketQuotes':
      return true;
    default:
      return false;
  }
}

export function applyPersistedWorkspaceTabsThunk(
  tabs: PersistedTab[],
  activeTabId: string | undefined,
  workspace: WorkspaceType,
  isEssentialTabsPanelShown?: boolean,
): Thunk<void> {
  return (dispatch, getState) => {
    dispatch(workspacePersistedTabsApplied(tabs, activeTabId, workspace, isEssentialTabsPanelShown));
    if (activeTabId === undefined) {
      return;
    }
    const state = getState();
    const tabType = getTabType(state, activeTabId);
    dispatch(workspaceTabActivated(activeTabId, workspace, tabType));
  };
}

export function workspaceTabCreatedThunk<W extends WorkspaceType>(
  workspace: W,
  tabType: TabsForWorkspace[W],
  insertAtStart = false,
): Thunk<void> {
  return (dispatch, _getState, { getNewGuid }) => {
    const tabId = getNewGuid();

    if (canWorkspaceBeEmpty(workspace) === false) {
      dispatch(workspaceTabCreated(tabId, workspace, tabType, undefined, insertAtStart));
    }

    switch (workspace) {
      case 'newBatch':
        dispatch(batchWorkspaceTabInitThunk(tabId));
        break;
      case 'static':
      case 'dynamic':
        dispatch(workspaceWithProductSelectorTabInitThunk(tabId, workspace));
        break;
    }
  };
}

export function batchWorkspaceTabInitThunk(tabId: string): Thunk<void> {
  return (dispatch, getState, { selectors: { getAvailableProductVariations } }) => {
    const state = getState();
    const availableProductVariations = getAvailableProductVariations(state, 'newBatch');
    if (availableProductVariations.length === 1) {
      dispatch(
        batchWorkspaceTabProductSelectionSubmittedThunk(
          tabId,
          availableProductVariations[0].productType,
          availableProductVariations[0].productSubType,
          availableProductVariations[0].wrapper,
          availableProductVariations[0].requestType,
        ),
      );
    }
  };
}
export function workspaceWithProductSelectorTabInitThunk(
  tabId: string,
  workspace: WorkspaceWithProductSelector,
): Thunk<void> {
  return (dispatch, getState, { selectors: { getIsUserInternal, getAvailableProductVariations } }) => {
    const state = getState();
    const availableProductVariations = getAvailableProductVariations(state, workspace);
    if (availableProductVariations.length === 1) {
      const userPreferredClient = (getIsUserInternal(state) && getUserPreferencesSelectedOnBehalfClient(state)) || null;

      dispatch(
        workspaceTabProductSelectionSubmitted(
          tabId,
          {
            productType: availableProductVariations[0].productType,
            productSubType: availableProductVariations[0].productSubType,
            wrapper: availableProductVariations[0].wrapper,
            requestType: availableProductVariations[0].requestType,
          },
          userPreferredClient,
          workspace,
        ),
      );
    }
  };
}

export function workspaceTabsAllRemovedThunk(workspace: WorkspaceType): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const tabIds = getWorkspaceTabIds(state, workspace);
    tabIds.forEach(tabId => {
      const tabType = getTabType(state, tabId);
      dispatch(workspaceTabRemovedCleanDataThunk(tabId, workspace, tabType));
    });

    dispatch(workspaceTabsAllRemoved(workspace, tabIds));
    if (isAutoCreateTabWorkspace(workspace)) {
      dispatch(workspaceTabCreatedThunk(workspace, 'quote'));
    }
  };
}

// TODO: Unit tests
export function workspaceTabRemovedThunk(
  tabId: string,
  workspace: WorkspaceType,
  persist: boolean = true,
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const tabIds = getWorkspaceTabIds(state, workspace);
    const tabType = getTabType(state, tabId);

    const activeTabId = getWorkspaceActiveTabId(state, workspace);
    if (workspace === 'newBatch') {
      dispatch(batchFormsAllDeletedThunk(tabId));
      dispatch(workspaceTabRemoved(tabId, workspace));
    } else {
      dispatch(workspaceTabRemovedCleanDataThunk(tabId, workspace, tabType));
      if (persist && workspace !== 'postTrade') {
        dispatch(workspaceTabDeleteSessionRequested(tabId, workspace));
      }
      dispatch(workspaceTabRemoved(tabId, workspace));
    }

    if (persist) {
      dispatch(workspaceTabPersistRequested(tabId, workspace));
    }
    dispatch(workspaceTabRemovedThunkGoToNewTab(tabId, tabIds, workspace, activeTabId));
  };
}

export function workspaceResetTabRequestedThunk(tabId: string, workspace: WorkspaceType): Thunk<void> {
  return (dispatch, getState, { selectors: { getFormQuoteId } }) => {
    const state = getState();
    const enrichedData = getFormEnrichedData(state, tabId);

    const quoteId = getFormQuoteId(state, workspace, tabId);
    const productType = enrichedData['ProductType'] as string;
    const productSubType = enrichedData['ProductSubtype'] as string;
    const wrapper = enrichedData['Wrapper'] as string;
    const selectedOnBehalfClient = enrichedData['SelectedOnBehalfClient'] as ISalesCounterpart;

    dispatch(
      workspaceResetTabRequested(tabId, workspace, productType, productSubType, wrapper, selectedOnBehalfClient),
    );
    if (quoteId !== undefined) {
      dispatch(notUsedQuoteCleanedThunk(tabId, quoteId));
    }
  };
}

function workspaceTabRemovedCleanDataThunk(tabId: string, workspace: WorkspaceType, tabType: TabType): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    if (workspace === 'static') {
      const staticQuoteId = getStaticFormInfos(state, tabId)?.quoteId;
      if (staticQuoteId) {
        dispatch(staticQuoteRemovedThunk(tabId, staticQuoteId));
      }
    }
    if (workspace === 'dynamic' || workspace === 'postTrade') {
      const dynamicQuoteId = getDynamicFormInfos(state, tabId)?.quoteId;
      if (dynamicQuoteId) {
        dispatch(dynamicQuoteRemovedThunk(tabId, dynamicQuoteId));
      }
    }
    if (workspace === 'postTrade') {
      const addonQuoteId = addonSelector.getLastQuoteId(state, tabId);
      if (addonQuoteId) {
        dispatch(addonQuoteRemoved(tabId, addonQuoteId));
      }
    }
    if (workspace === 'secondaryMarketQuotes') {
      const productKey = getProductKey(state, tabId)!;
      dispatch(secondaryMarketQuoteWorkspaceRemoveTab(tabId, tabType as SecondaryMarketQuotesTabType, productKey));
    }
    if (workspace === 'secondaryMarketPostEvents') {
      dispatch(secondaryMarketPostEventWorkspaceRemoveTab(tabId, tabType as SecondaryMarketPostEventTabType));
    }
  };
}

function workspaceTabRemovedThunkGoToNewTab<W extends WorkspaceType>(
  tabId: string,
  tabIds: string[],
  workspace: W,
  activeTabId?: string,
): Thunk<void> {
  return dispatch => {
    const isSingleTab = tabIds.length === 1;
    const tabIndex = tabIds.indexOf(tabId);
    const isLastTab = tabIndex === tabIds.length - 1;
    const nextTabIndex = isLastTab ? tabIndex - 1 : tabIndex + 1;

    if (isSingleTab && isAutoCreateTabWorkspace(workspace)) {
      dispatch(workspaceTabCreatedThunk(workspace, undefined as any));
    } else {
      if (tabId === activeTabId) {
        const nextTabId = tabIds[nextTabIndex];

        if (nextTabId) {
          dispatch(workspaceTabActivated(nextTabId, workspace, undefined as TabsForWorkspace[W]));
        }
      }
    }
  };
}

export function workspaceTabsFetchedThunk(
  tabs: PersistedTab[],
  activeTabId: string | undefined,
  workspace: WorkspaceType,
  tabFetchRequestId?: string,
  isEssentialTabsPanelShown?: boolean,
): Thunk<void> {
  return dispatch => {
    dispatch(workspaceTabsFetched(tabs, activeTabId, workspace, tabFetchRequestId, isEssentialTabsPanelShown));

    if (workspace === 'ordersBasket') {
      tabs.forEach(tab => {
        dispatch(ordersBasketTabContentFetchRequested(tab.id));
      });
    }
  };
}

export function workspaceTabReloadRequestedThunk(
  workspace: WorkspaceType,
  productId: string,
  quoteId: string,
  pricerRfqId: string,
  processingChain: ProcessingChain,
  withLastQuote: boolean = true,
  externalQuoteId?: string,
  tabName?: string,
): Thunk<void> {
  return (dispatch, getState, { getNewGuid }) => {
    const state = getState();

    if (workspace !== 'newBatch') {
      dispatch(
        workspaceTabQuoteRepriced(
          getNewGuid(),
          productId,
          IdType.ProductId,
          quoteId,
          pricerRfqId,
          workspace,
          withLastQuote,
          processingChain,
          externalQuoteId,
        ),
      );
      return;
    }

    const blotterItem = getBlotterPanelItem(state);
    if (
      !blotterItem?.productType ||
      !blotterItem?.productSubType ||
      !blotterItem?.wrapper ||
      blotterItem?.quoteId !== quoteId
    ) {
      return;
    }

    const matchedTabId = getBatchTabIdToReloadForm(
      state,
      blotterItem?.productType!,
      blotterItem?.productSubType!,
      blotterItem?.wrapper!,
    );
    const tabId = matchedTabId ?? getNewGuid();

    if (!matchedTabId) {
      dispatch(workspaceTabCreated(tabId, 'newBatch', 'reprice'));
      dispatch(
        batchWorkspaceProductSelectedThunk(
          tabId,
          blotterItem?.productType!,
          blotterItem?.productSubType!,
          blotterItem?.wrapper!,
          undefined,
          tabName,
          true,
        ),
      );
    }

    dispatch(workspaceTabActivated(tabId, 'newBatch', 'reprice'));
    const id = productId ?? quoteId;
    const idType = productId ? IdType.ProductId : IdType.Guess;
    dispatch(
      batchFormQuoteReloadRequested(
        tabId,
        getNewGuid(),
        id,
        idType,
        blotterItem?.productType,
        blotterItem?.productSubType,
        blotterItem?.wrapper,
        blotterItem?.quoteId,
        blotterItem?.pricerRfqId,
        blotterItem?.processingChain,
      ),
    );
  };
}

export function workspaceRfqLoadRequestedThunk(rfqId: string): Thunk<void> {
  return (dispatch, _getState, { getNewGuid }) => {
    dispatch(workspaceRfqLoadRequested(getNewGuid(), rfqId, 'static'));
  };
}

export function workspaceProductVariationSelectedThunk(
  workspace: WorkspaceWithProductSelector,
  selectedProductVariation: Partial<SelectedProductVariation>,
  intl: IntlShape,
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const currPayOffType = selectedProductVariation.payoffType;
    const prevPayOffType = getSelectedProductVariation(state, workspace, intl)?.payoffType;

    if (currPayOffType || !prevPayOffType) {
      dispatch(workspaceProductVariationSelected(workspace, selectedProductVariation));
      return;
    }

    if (
      prevPayOffType &&
      getAvailablePayoffTypes(state, workspace, selectedProductVariation).includes(prevPayOffType)
    ) {
      dispatch(
        workspaceProductVariationSelected(workspace, { ...selectedProductVariation, payoffType: prevPayOffType }),
      );
      return;
    }

    dispatch(workspaceProductVariationSelected(workspace, selectedProductVariation));
  };
}

export function workspaceTabProductVariationSelectedThunk(
  tabId: string,
  selectedProductVariation: Partial<ProductVariation>,
  workspaceType: WorkspaceType,
): Thunk<void> {
  return (dispatch, getState, { selectors: { getAvailableProductVariations } }) => {
    const state = getState();
    const productVariations = getAvailableProductVariations(state, workspaceType);

    const autoSelectedProductSubType =
      selectedProductVariation.productSubType ??
      tryGetSingleProductSubType(productVariations, selectedProductVariation.productType);

    const autoSelectedWrapper =
      selectedProductVariation.wrapper ??
      tryGetSingleWrapper(productVariations, selectedProductVariation.productType, autoSelectedProductSubType);

    const autoSelectedProductVariation: Partial<ProductVariation> = {
      productType: selectedProductVariation.productType,
      productSubType: autoSelectedProductSubType,
      wrapper: autoSelectedWrapper,
    };

    dispatch(workspaceTabProductVariationSelected(tabId, autoSelectedProductVariation, workspaceType));
  };
}

function tryGetSingleProductSubType(productVariations: readonly ProductVariation[], selectedProductType?: string) {
  if (selectedProductType === undefined) {
    return undefined;
  }

  const filteredProductSubTypes = productVariations
    .filter(({ productType }) => selectedProductType === productType)
    .map(({ productSubType }) => productSubType);

  const distinctProductSubTypes = [...new Set(filteredProductSubTypes)];

  return distinctProductSubTypes.length === 1 ? filteredProductSubTypes[0] : undefined;
}

function tryGetSingleWrapper(
  productVariations: readonly ProductVariation[],
  selectedProductType?: string,
  selectedProductSubType?: string,
) {
  if (selectedProductType === undefined || selectedProductSubType === undefined) {
    return undefined;
  }

  const filteredWrappers = productVariations
    .filter(
      ({ productType, productSubType }) =>
        selectedProductType === productType && selectedProductSubType === productSubType,
    )
    .map(({ wrapper }) => wrapper);

  const distinctWrappers = [...new Set(filteredWrappers)];

  return distinctWrappers.length === 1 ? filteredWrappers[0] : undefined;
}
